import { useEffect } from 'react';
import type { InventoryItem, SettlementVenue } from '@/types';
import { prefillFromInventory, type TradeInventoryItem } from '@/services/trades/trade-service';
import { marketDataService } from '@/services/market-data/market-data-service';
import { GROSS_PRICE_FIELD_MARKER, type TradeForm } from '../../components/trade-form-sections/types';
import type { SearchInventoryItem } from '../useCreateTradeForm';
import { getDefaultBenchmarkByCurrency } from './benchmark-defaults';

/**
 * External "Create trade from inventory row" path: when the store sentinel
 * `selectedItemForTrade` is set, mirror it into the form (security / position /
 * rate / settlement / dates), set the selected inventory item + search display,
 * then clear the sentinel. Extracted verbatim from useCreateTradeForm (LR-495).
 */
export function useSelectedItemForTradePrefill({
  form,
  selectedItemForTrade,
  setSelectedItemForTrade,
  benchmarkRates,
  setSelectedInventoryItem,
  setSecuritySearchQuery,
}: {
  form: TradeForm;
  selectedItemForTrade: InventoryItem | null;
  setSelectedItemForTrade: (item: InventoryItem | null) => void;
  benchmarkRates: Record<string, number>;
  setSelectedInventoryItem: (item: SearchInventoryItem | null) => void;
  setSecuritySearchQuery: (query: string) => void;
}) {
  useEffect(() => {
    if (selectedItemForTrade) {
      const tradeItem: TradeInventoryItem = {
        legalEntity: selectedItemForTrade.legal_entity,
        accountId: selectedItemForTrade.account_id,
        counterparty: selectedItemForTrade.counterparty,
        symbolId: selectedItemForTrade.symbol_id,
        shortName: selectedItemForTrade.short_name,
        sedol: selectedItemForTrade.sedol,
        isin: selectedItemForTrade.isin,
        type: selectedItemForTrade.type,
        price: parseFloat(selectedItemForTrade.price) || 0,
        quantity: selectedItemForTrade.quantity,
        currency: selectedItemForTrade.currency,
        leverageRate: parseFloat(selectedItemForTrade.leverage_rate) || 0,
        benchmarkFee: parseFloat(selectedItemForTrade.benchmark_fee) || 0,
      };
      const prefilled = prefillFromInventory(tradeItem, benchmarkRates);
      const searchItem: SearchInventoryItem = {
        legalEntity: selectedItemForTrade.legal_entity,
        accountId: selectedItemForTrade.account_id,
        counterparty: selectedItemForTrade.counterparty,
        type: selectedItemForTrade.type as 'STOCK' | 'BOND' | 'ETF',
        sedol: selectedItemForTrade.sedol,
        isin: selectedItemForTrade.isin,
        symbolId: selectedItemForTrade.symbol_id,
        shortName: selectedItemForTrade.short_name,
        price: parseFloat(selectedItemForTrade.price) || 0,
        currency: selectedItemForTrade.currency,
        quantity: selectedItemForTrade.quantity,
        benchmarkFee: parseFloat(selectedItemForTrade.benchmark_fee) || 0,
      };
      setSelectedInventoryItem(searchItem);
      const displayText = searchItem.shortName && searchItem.shortName !== searchItem.symbolId
        ? `${searchItem.shortName} (${searchItem.symbolId})`
        : searchItem.symbolId;
      setSecuritySearchQuery(displayText);
      if (prefilled.security?.symbol) {
        form.setFieldValue('symbol_id', prefilled.security.symbol);
      }
      if (prefilled.position?.contractPrice) {
        form.setFieldValue('gross_price', snapshotGrossPrice(prefilled.position.contractPrice));
      }
      if (prefilled.position?.tradeType) {
        form.setFieldValue('trade_type', prefilled.position.tradeType);
      }
      if (prefilled.rate?.rateType) {
        form.setFieldValue('rate_type', prefilled.rate.rateType);
      }
      if (prefilled.rate?.benchmark) {
        form.setFieldValue('benchmark_name', prefilled.rate.benchmark);
      }
      if (prefilled.rate?.fixedRate) {
        form.setFieldValue('benchmark_rate', prefilled.rate.fixedRate.toString());
      }
      if (prefilled.settlement?.venue) {
        form.setFieldValue('settlement_venue', prefilled.settlement.venue);
      }
      if (prefilled.settlement?.deliveryType) {
        form.setFieldValue('delivery_type', prefilled.settlement.deliveryType);
      }
      // fx_rate belongs to the currency pair: written alone it would pair 1.0
      // with whatever settlement currency the form already holds.
      if (prefilled.settlement?.settlementCurrency) {
        form.setFieldValue('settlement_currency', prefilled.settlement.settlementCurrency);
        form.setFieldValue('fx_rate', prefilled.settlement.fxRate);
      }
      if (prefilled.rate?.dividendPassThrough) {
        form.setFieldValue('dividend_passthrough_percent', prefilled.rate.dividendPassThrough.toString());
      }
      // The time travels with the date: alone, the new date would pair with the time
      // the form opened at, which may be the day before.
      if (prefilled.dates?.tradeDate && prefilled.dates.tradeTime) {
        form.setFieldValue('trade_date', prefilled.dates.tradeDate);
        form.setFieldValue('trade_time', prefilled.dates.tradeTime);
      }
      if (prefilled.dates?.settlementDate) {
        form.setFieldValue('settlement_date', prefilled.dates.settlementDate);
      }
      if (prefilled.dates?.tradeDuration) {
        form.setFieldValue('trade_duration', prefilled.dates.tradeDuration);
      }
      if (prefilled.dates?.noticePeriodDays !== undefined) {
        form.setFieldValue('notice_period_days', prefilled.dates.noticePeriodDays);
      }
      setSelectedItemForTrade(null);
    }
    // stable setState setters omitted to keep the original dependency array.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedItemForTrade, benchmarkRates, form, setSelectedItemForTrade]);
}

/**
 * LR-621: both selection paths collapse a missing inventory-snapshot price to
 * 0 (`inventoryItem?.price || 0` in the search index, `parseFloat(...) || 0`
 * in the inventory-row mirror), and `(0).toFixed(2)` then prefilled a
 * confident-looking "0.00" into a money field — which stayed there whenever
 * the async COB overwrite didn't land (COB 404 / negative cache). An absent
 * price must present as an absent value: '' shows the absence honestly
 * (both '' and "0.00" trip the required validator — the difference is what
 * the operator SEES) and is the untouched baseline the COB overwrite lands on.
 * The gate looks at what LANDS in the precision=2 field, not the raw price
 * (review kvi #2 rule, mirrored from the COB guard below): a sub-cent
 * snapshot (0 < price < 0.005) rounds to "0.00" and must prefill '' too,
 * not a bookable-looking zero. Shared by every synchronous gross_price
 * write site and the COB untouched-guard so they cannot drift apart (hook
 * order stays a non-invariant, review LR-578 r2).
 */
export const snapshotGrossPrice = (price: number): string => {
  const formatted = price.toFixed(2);
  return parseFloat(formatted) > 0 ? formatted : '';
};

/**
 * When a security is selected (inventory pick), align venue / currency / fx /
 * rate-type / benchmark to that security's currency defaults. benchmark_rate is
 * left to useBenchmarkRateSync (off benchmark_name), so a late benchmarks fetch
 * doesn't reset this block. Extracted verbatim from useCreateTradeForm (LR-495).
 */
export function useInventoryDefaults({
  form,
  selectedInventoryItem,
}: {
  form: TradeForm;
  selectedInventoryItem: SearchInventoryItem | null;
}) {
  useEffect(() => {
    if (selectedInventoryItem) {
      const settlementVenueMap: Record<string, SettlementVenue> = {
        USD: 'dtc',
        EUR: 'euroclear',
      };
      const defaultVenue = selectedInventoryItem.currency ? settlementVenueMap[selectedInventoryItem.currency] || 'dtc' : 'dtc';
      const defaultBenchmark = getDefaultBenchmarkByCurrency(selectedInventoryItem.currency || 'USD');
      form.setFieldValue('symbol_id', selectedInventoryItem.symbolId);
      form.setFieldValue('gross_price', snapshotGrossPrice(selectedInventoryItem.price));
      if (selectedInventoryItem.currency) {
        form.setFieldValue('settlement_venue', defaultVenue);
        // Both selection paths land here. The payload's `currency` is read from
        // this field, so it must match the security currency the form shows.
        form.setFieldValue('currency', selectedInventoryItem.currency);
        form.setFieldValue('settlement_currency', selectedInventoryItem.currency);
        form.setFieldValue('fx_rate', 1.0);
        form.setFieldValue('net_settlement_converted_input', undefined);
        form.setFieldValue('rate_type', 'floating');
        form.setFieldValue('benchmark_name', defaultBenchmark);
        // benchmark_rate is populated by useBenchmarkRateSync (LR-435), keyed off
        // benchmark_name, so a late benchmarks fetch doesn't reset this block.
        form.setFieldValue('dividend_passthrough_percent', '100');
      }
    }
  }, [selectedInventoryItem, form]);
}

/**
 * LR-578: async Gross Price prefill from the canonical COB T-1 close — the same
 * source the Trades blotter attaches as "COB Gross Price", so a trade booked off
 * the prefill no longer diverges from the blotter (feedback p.58). Both selection
 * flows (search dropdown and "Create Trade" from an Inventory row) converge on
 * `selectedInventoryItem`, so one effect covers both. The synchronous
 * inventory-snapshot prefill (written by the selection handler and re-written
 * by useInventoryDefaults via `snapshotGrossPrice`) stays as the instant value
 * and remains the fallback when no COB exists (404 → fetchCob soft-fails to
 * null); an absent snapshot prefills EMPTY rather than "0.00" (LR-621), so a
 * dropped COB never leaves a bookable-looking zero behind.
 *
 * Three guards on the async overwrite:
 *  - stale (LR-378 pattern): the operator switched securities before the fetch
 *    resolved — the form's symbol_id no longer matches the fetched symbol;
 *  - untouched: the operator already edited Gross Price — the field no longer
 *    equals the synchronous baseline `snapshotGrossPrice(item.price)`. For a
 *    zero-snapshot item that baseline is '', so an operator who typed and
 *    then CLEARED the field inside the fetch window reads as untouched and
 *    gets the COB — accepted trade-off (LR-621): the value lands visibly in
 *    an empty required field (nothing is silently re-priced). A dirty-meta
 *    gate could close even that window (form-core setFieldValue accepts
 *    dontUpdateMeta), but it would stop the prefill writes from marking the
 *    field touched/dirty — changing error-display semantics — to cover a
 *    sub-second race with an acceptable outcome; not worth the plumbing
 *    (review r2);
 *  - focused (review kvi #1): the operator is inside the Gross Price input.
 *    DecimalInput skips its external-value sync while focused (LR-371), so the
 *    write would change the booked value while the visible string stayed at the
 *    inventory price — and Enter submits from a focused input without blurring.
 *    Skipping loses the COB prefill for that one selection, which is the same
 *    outcome the untouched-guard produces the moment the operator types; a
 *    money field showing one number and booking another is the worse failure.
 */
export function useCobGrossPricePrefill({
  form,
  selectedInventoryItem,
}: {
  form: TradeForm;
  selectedInventoryItem: SearchInventoryItem | null;
}) {
  useEffect(() => {
    if (!selectedInventoryItem) return;
    const targetSymbolId = selectedInventoryItem.symbolId;
    const syncBaseline = snapshotGrossPrice(selectedInventoryItem.price);
    marketDataService.fetchCob(targetSymbolId).then((cob) => {
      if (!cob) return;
      if (form.state.values.symbol_id !== targetSymbolId) return;
      if (form.state.values.gross_price !== syncBaseline) return;
      const active = document.activeElement;
      if (active instanceof HTMLElement && active.dataset.field === GROSS_PRICE_FIELD_MARKER) return;
      // review kvi #2: compare what actually LANDS in the field, not the raw
      // close. The field is precision=2, so a sub-cent close (0 < cob < 0.005)
      // clears fetchCob's `<= 0` guard yet rounds to "0.00" — dropping it
      // keeps the snapshot value (or the empty field) instead. Same predicate
      // as the synchronous write sites: snapshotGrossPrice returns '' for
      // exactly that band, so a future precision change lands in one place
      // (review r2 — this was the last hand-rolled copy of the gate).
      const cobFormatted = snapshotGrossPrice(cob.cob_price);
      if (!cobFormatted) return;
      form.setFieldValue('gross_price', cobFormatted);
    });
  }, [selectedInventoryItem, form]);
}
